/**
 * Amasty Multiple Wishlist compatibility for ScandiPWA
 * @copyright Scandiweb, Inc. All rights reserved.
 */

import { ERROR_CONFIGURABLE_NOT_PROVIDED } from 'Component/ProductWishlistButton/ProductWishlistButton.config';
import { showNotification } from 'Store/Notification/Notification.action';
import { hideActiveOverlay } from 'Store/Overlay/Overlay.action';
import { showPopup } from 'Store/Popup/Popup.action';
import BrowserDatabase from 'Util/BrowserDatabase';
import { getIndexedProduct } from 'Util/Product';
import getStore from 'Util/Store';

import {
    PRODUCT_PAGE_POPUP_ID,
    WISHLIST_ADD,
    WISHLIST_COPY,
    WISHLIST_ITEMS,
    WISHLIST_MOVE,
    WISHLIST_OPERATION_COPY,
    WISHLIST_OPERATION_EDIT,
    WISHLIST_OPERATION_MOVE,
} from '../component/AmastyWishlistItem/AmastyWishlistItem.config';
import {
    AMASTY_OPERATION, AMASTY_PRODUCT, AMASTY_WISHLIST, WISHLISTS_POPUP_ID,
} from '../component/MultipleWishlist/MultipleWishlist.config';

export const WishlistDispatcher = import(
    /* webpackMode: "lazy", webpackChunkName: "dispatchers" */
    'Store/Wishlist/Wishlist.dispatcher'
);

export const PRODUCTS_IN_WISHLIST = 'wishlist_products';

/** @namespace Scandiweb/AmastyMultipleWishlist/Util/MultipleWishlist/getWishlist */
export const getWishlist = (wishlistItem) => {
    if (!wishlistItem) {
        return {};
    }

    const {
        id,
        sku,
        description,
        qty,
        product,
    } = wishlistItem;

    const wishlist = {
        id,
        sku,
        quantity: qty,
        description,
    };

    return { ...product, wishlist };
};

/** @namespace Scandiweb/AmastyMultipleWishlist/Util/MultipleWishlist/getCompatibleWishlist */
export const getCompatibleWishlist = (wishlist) => {
    if (!Object.keys(wishlist).length) {
        return [];
    }

    const productsToAdd = wishlist.items.reduce((prev, wishlistItem) => {
        const {
            id,
            sku,
            product,
            description,
            qty: quantity,
        } = wishlistItem;

        return {
            ...prev,
            [id]: {
                ...product,
                quantity,
                wishlist: {
                    id,
                    sku,
                    quantity,
                    description,
                },
            },
        };
    }, {});

    BrowserDatabase.setItem(productsToAdd, PRODUCTS_IN_WISHLIST);

    return productsToAdd;
};

/** @namespace Scandiweb/AmastyMultipleWishlist/Util/MultipleWishlist/showProductPagePopup */
export const showProductPagePopup = (index = '') => {
    const { dispatch } = getStore();
    BrowserDatabase.setItem(WISHLIST_OPERATION_EDIT, AMASTY_OPERATION);

    dispatch(showPopup(PRODUCT_PAGE_POPUP_ID + index, { title: __('Edit Options') }));
};

/** @namespace Scandiweb/AmastyMultipleWishlist/Util/MultipleWishlist/showWishlistsPopup */
export const showWishlistsPopup = (index = '') => {
    const { dispatch } = getStore();

    dispatch(showPopup(WISHLISTS_POPUP_ID + index, { title: __('Please choose a List') }));
};

/** @namespace Scandiweb/AmastyMultipleWishlist/Util/MultipleWishlist/closePopup */
export const closePopup = async () => {
    const { dispatch } = getStore();

    BrowserDatabase.setItem(WISHLIST_OPERATION_MOVE, AMASTY_OPERATION);
    dispatch(hideActiveOverlay());
};

/** @namespace Scandiweb/AmastyMultipleWishlist/Util/MultipleWishlist/getIsWishlistPopup */
export const getIsWishlistPopup = () => {
    const wishlistPopup = getStore().getState().PopupReducer?.popupPayload || {};

    return Object.keys(wishlistPopup)
        .some(
            (item) => item.toString() === WISHLIST_COPY
                || item.toString() === WISHLIST_MOVE
                || item.toString() === WISHLIST_ADD
        );
};

/** @namespace Scandiweb/AmastyMultipleWishlist/Util/MultipleWishlist/getProductVariant */
export const getProductVariant = (product, configurableVariantIndex) => {
    const { type_id } = product;

    if (type_id === 'configurable') {
        if (configurableVariantIndex < 0) {
            return ERROR_CONFIGURABLE_NOT_PROVIDED;
        }

        // const extension_attributes = getExtensionAttributes({ ...product, configurableVariantIndex });
        const variant = product.variants[configurableVariantIndex];
        //
        // return { ...variant, product_option: { extension_attributes } };

        return { ...variant, product_option: {} };
    }

    return product;
};

/** @namespace Scandiweb/AmastyMultipleWishlist/Util/MultipleWishlist/addProductToWishlist */
export const addProductToWishlist = async (
    wishlistId,
    wishlistItem,
    name = '',
    operation = 'copy',
    isSingle = true
) => {
    const { dispatch } = getStore();
    const wishlistDispatcher = (await WishlistDispatcher).default;
    const product = { wishlistId, ...wishlistItem };

    try {
        await wishlistDispatcher.addItemToWishlist(dispatch, product);

        if (isSingle) {
            if (operation === WISHLIST_OPERATION_COPY) {
                dispatch(showNotification('success', __('Successfuly copied item to wishlist %s', name)));

                return;
            }

            dispatch(showNotification('success', __('Successfuly moved item to wishlist %s', name)));
        }
    } catch (e) {
        dispatch(showNotification('error', __('%s', e)));
    }
};

/** @namespace Scandiweb/AmastyMultipleWishlist/Util/MultipleWishlist/removeProductFromWishlist */
export const removeProductFromWishlist = async (wishlistId, wishlistItem, isSingle = true) => {
    const { dispatch } = getStore();
    const wishlistDispatcher = (await WishlistDispatcher).default;
    const { wishlist: { id: item_id } } = wishlistItem;
    const noMessages = true;
    const options = { item_id, noMessages, wishlistId };

    try {
        await wishlistDispatcher.removeItemFromWishlist(dispatch, options);

        if (isSingle) {
            dispatch(showNotification('success', __('Items were deleted successfully')));
        }
    } catch (e) {
        dispatch(showNotification('error', e));
    }
};

/** @namespace Scandiweb/AmastyMultipleWishlist/Util/MultipleWishlist/getConfigurableIndex */
export const getConfigurableIndex = (product, sku) => {
    const { variants = [] } = product;
    const conf = variants.map((item, idx) => {
        if (item.sku === sku) {
            return idx;
        }

        return null;
    }).filter((item) => item !== null)[0];

    return conf;
};

/** @namespace Scandiweb/AmastyMultipleWishlist/Util/MultipleWishlist/addProductToWishlistFromSearchOverlay */
export const addProductToWishlistFromSearchOverlay = (wishlistId) => {
    const { product, quantity } = BrowserDatabase.getItem(AMASTY_PRODUCT) || {};
    const {
        sku,
        description = null,
    } = product;

    const configurableIndex = getConfigurableIndex(product, sku);
    const wishlistProduct = getProductVariant(product, configurableIndex);
    const { product_option } = wishlistProduct;

    addProductToWishlist(wishlistId, {
        sku,
        product_option,
        quantity,
        description,
    });

    BrowserDatabase.deleteItem(AMASTY_PRODUCT);
    closePopup();
};

/** @namespace Scandiweb/AmastyMultipleWishlist/Util/MultipleWishlist/addProductToWishlistFromProductPage */
export const addProductToWishlistFromProductPage = (wishlistId, name) => {
    const {
        sku,
        quantity = 1,
        description = null,
        product_option,
    } = BrowserDatabase.getItem('AmastyWishlistProduct') || {};

    addProductToWishlist(wishlistId, {
        sku,
        product_option,
        quantity,
        description,
    }, name);

    BrowserDatabase.deleteItem('AmastyWishlistProduct');
    closePopup();
};

/** @namespace Scandiweb/AmastyMultipleWishlist/Util/MultipleWishlist/copySelected */
export const copySelected = (item, wishlistItems) => {
    const { wishlistId, name } = item;
    const isSingle = wishlistItems.length === 1;
    const { dispatch } = getStore();

    wishlistItems.map((wishlistItem) => {
        const {
            product,
            product: { sku: productSku } = {},
            sku,
            description,
        } = wishlistItem || {};

        const indexedProduct = getIndexedProduct(product);
        const configurableIndex = getConfigurableIndex(indexedProduct, sku);
        const wishListProduct = getProductVariant(indexedProduct, configurableIndex);
        const { product_option } = wishListProduct;
        const quantity = wishlistItem.quantity || wishlistItem.qty;

        addProductToWishlist(wishlistId, {
            sku: productSku,
            product_option,
            quantity,
            description,
        }, name, '', isSingle);

        return null;
    });

    if (!isSingle) {
        dispatch(showNotification('success', __('Items were copied successfully')));
    }

    BrowserDatabase.deleteItem(WISHLIST_ITEMS);
    closePopup();
};

/** @namespace Scandiweb/AmastyMultipleWishlist/Util/MultipleWishlist/copyWishlist */
export const copyWishlist = (item, wishlistItem) => {
    const { wishlistId, name } = item;

    const wishlistItems = BrowserDatabase.getItem(WISHLIST_ITEMS);

    if (wishlistItems && wishlistItems.length) {
        copySelected(item, wishlistItems);

        return;
    }

    const { sku: productSku, wishlist: { sku, quantity, description } = {} } = wishlistItem || {};

    if (!productSku) {
        addProductToWishlistFromProductPage(wishlistId, name);

        return;
    }

    const configurableIndex = getConfigurableIndex(wishlistItem, sku);
    const product = getProductVariant(wishlistItem, configurableIndex);
    const { product_option } = product;

    addProductToWishlist(wishlistId, {
        sku: productSku,
        product_option,
        quantity,
        description,
    }, name);

    closePopup();
};

/** @namespace Scandiweb/AmastyMultipleWishlist/Util/MultipleWishlist/moveSelected */
export const moveSelected = (item, wishlistItems) => {
    const { wishlistId, name } = item;
    const isSingle = wishlistItems.length === 1;
    const { dispatch } = getStore();

    wishlistItems.map((wishlistItem) => {
        const {
            product,
            product: { sku: productSku } = {},
            sku,
            description,
        } = wishlistItem || {};

        const indexedProduct = getIndexedProduct(product);
        const configurableIndex = getConfigurableIndex(indexedProduct, sku);
        const wishListProduct = getProductVariant(indexedProduct, configurableIndex);
        const { product_option } = wishListProduct;
        const quantity = wishlistItem.quantity || wishlistItem.qty;
        const fromWishlistId = BrowserDatabase.getItem(AMASTY_WISHLIST);
        const itemsToRemove = BrowserDatabase.getItem(PRODUCTS_IN_WISHLIST);
        const itemToRemove = Object.values(itemsToRemove).find((item) => item.sku === productSku);

        addProductToWishlist(wishlistId, {
            sku: productSku,
            product_option,
            quantity,
            description,
        }, name, WISHLIST_OPERATION_MOVE, isSingle);

        removeProductFromWishlist(fromWishlistId, itemToRemove, isSingle);

        return null;
    });

    BrowserDatabase.deleteItem(WISHLIST_ITEMS);
    BrowserDatabase.setItem(WISHLIST_OPERATION_MOVE, AMASTY_OPERATION);

    if (!isSingle) {
        dispatch(showNotification('success', __('Items were moved successfully')));
    }
    closePopup();
};

/** @namespace Scandiweb/AmastyMultipleWishlist/Util/MultipleWishlist/moveWishlist */
export const moveWishlist = (item, wishlistItem) => {
    const wishlistItems = BrowserDatabase.getItem(WISHLIST_ITEMS);

    if (wishlistItems && wishlistItems.length) {
        moveSelected(item, wishlistItems);

        return;
    }

    const { wishlistId, name } = item;
    const { sku: productSku, wishlist: { sku, quantity, description } } = wishlistItem;
    const configurableIndex = getConfigurableIndex(wishlistItem, sku);
    const product = getProductVariant(wishlistItem, configurableIndex);
    const { product_option } = product;
    const fromWishlistId = BrowserDatabase.getItem(AMASTY_WISHLIST);

    addProductToWishlist(wishlistId, {
        sku: productSku,
        product_option,
        quantity,
        description,
    }, name, WISHLIST_OPERATION_MOVE);

    removeProductFromWishlist(fromWishlistId, wishlistItem);
    BrowserDatabase.setItem(WISHLIST_OPERATION_MOVE, AMASTY_OPERATION);
    closePopup();
};

/** @namespace Scandiweb/AmastyMultipleWishlist/Util/MultipleWishlist/isEnabled */
export const isEnabled = () => getStore().getState().ConfigReducer?.amastyMWishlistConfig?.isEnabled;

export const operationMap = {
    [WISHLIST_COPY]: copyWishlist,
    [WISHLIST_MOVE]: moveWishlist,
};
